import React, { useState, useEffect, useMemo } from 'react';
import { 
  Clock, MapPin, User, Car, Phone, Check, X, Printer, 
  Search, Calendar, Zap, Eye, UserPlus, RefreshCw, Trash2
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { getRideDestination, getRidePickup, getRideScheduledAt, isScheduledRide } from '../../utils/rideFormat';
import { formatRidePriceLek, getRidePriceLek } from '../../utils/ridePrice';
import { useTableSort, sortRows } from '../../hooks/useTableSort';
import { SortableTableHead } from '../ui/SortableTableHead';
import { getAdAttribution } from '../../utils/adSource';

const RIDE_TABLE_SORT = {
  id: (r) => r.ticket_number || r.ride_number || '',
  customer: (r) => (r.passenger_name || '').toLowerCase(),
  bookedAt: (r) => new Date(r.created_at || 0).getTime(),
  destination: (r) => getRideDestination(r).toLowerCase(),
  driver: (r) => (r.driver_name || '').toLowerCase(),
  tripAt: (r) => {
    const t = getRideScheduledAt(r);
    return t ? new Date(t).getTime() : 0;
  },
  price: (r) => {
    const lek = getRidePriceLek(r);
    return lek == null ? -1 : Number(lek);
  },
  status: (r) => (r.status || '').toLowerCase(),
};

const getRideSource = (ride = {}) => {
  const src = (ride.source || '').toLowerCase();
  const passengerType = (ride.passenger_type || '').toLowerCase();
  if (src.includes('website') || passengerType === 'website') {
    return { label: 'Website', className: 'bg-purple-100 text-purple-700' };
  }
  if (src.includes('passenger_app') || passengerType === 'app') {
    return { label: 'App', className: 'bg-blue-100 text-blue-700' };
  }
  if (src.includes('dispatcher') || src.includes('walk-in')) {
    return { label: 'Dispatcher', className: 'bg-gray-100 text-gray-700' };
  }
  return null;
};

const statusConfig = {
  pending: { label: 'Në Pritje', labelEn: 'Pending', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  accepted: { label: 'Pranuar', labelEn: 'Accepted', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  assigned: { label: 'Caktuar', labelEn: 'Assigned', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  arriving: { label: 'Duke Arritur', labelEn: 'Arriving', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  en_route: { label: 'Tek Pasagjeri', labelEn: 'To Pickup', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  arrived_pickup: { label: 'Tek Pickup', labelEn: 'At Pickup', bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  passenger_onboard: { label: 'Pasagjeri Hipi', labelEn: 'Passenger Onboard', bg: 'bg-cyan-100', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  in_progress: { label: 'Në Udhëtim', labelEn: 'In Progress', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  arrived_destination: { label: 'Tek Destinacioni', labelEn: 'At Destination', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  completed: { label: 'Përfunduar', labelEn: 'Completed', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  cancelled: { label: 'Anuluar', labelEn: 'Cancelled', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  scheduled: { label: 'Planifikuar', labelEn: 'Scheduled', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' }
};

const ACTIVE_STATUSES = ['pending', 'assigned', 'en_route', 'accepted', 'arriving', 'in_progress', 'arrived_pickup'];

const ActiveOrdersTable = ({ 
  rides = [], 
  onAutoAssign, 
  onAssign,
  onCancel, 
  onComplete, 
  onPrint,
  onViewDetails,
  onDelete,
  onRefresh,
  loading = false,
  queueHasDrivers = false,
  showAutoAssign = true,
  showDelete = false,
  title = 'Porositë Aktive',
  defaultStatusFilter = 'all',
}) => {
  const { formatDateTime } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const { sortKey, sortDir, onSort } = useTableSort('bookedAt', 'desc');

  useEffect(() => {
    setStatusFilter(defaultStatusFilter);
  }, [defaultStatusFilter]);

  // Filter rides
  const filteredRides = rides.filter(ride => {
    const destination = getRideDestination(ride);
    const matchesSearch = !searchTerm || 
      ride.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ride.ride_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ride.passenger_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ride.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      destination.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'active'
        ? ACTIVE_STATUSES.includes(ride.status)
        : statusFilter === 'scheduled'
          ? isScheduledRide(ride)
          : ride.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const displayedRides = useMemo(
    () => sortRows(filteredRides, sortKey, sortDir, RIDE_TABLE_SORT),
    [filteredRides, sortKey, sortDir],
  );

  // Get status config
  const getStatus = (status) => statusConfig[status] || statusConfig.pending;

  const activeCount = rides.filter(r => ACTIVE_STATUSES.includes(r.status)).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            {title} ({activeCount})
          </h3>
          
          <div className="flex items-center gap-2">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition"
                title="Rifresko"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Kërko..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border rounded-lg w-48 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            >
              <option value="all">Të Gjitha</option>
              <option value="active">Aktive</option>
              <option value="pending">Në Pritje</option>
              <option value="assigned">Caktuar</option>
              <option value="accepted">Pranuar</option>
              <option value="arriving">Duke Arritur</option>
              <option value="en_route">Në Rrugë</option>
              <option value="in_progress">Në Udhëtim</option>
              <option value="completed">Përfunduar</option>
              <option value="cancelled">Anuluar</option>
              <option value="scheduled">Rezervime</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableTableHead label="ID" field="id" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Klienti" field="customer" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Data/Ora e rezervimit" field="bookedAt" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Destinacioni" field="destination" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Shoferi" field="driver" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Data/Ora e udhetimit" field="tripAt" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTableHead label="Çmimi" field="price" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortableTableHead label="Statusi" field="status" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
              <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Veprime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && displayedRides.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-12 text-gray-400">
                  <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Duke ngarkuar porositë...</p>
                </td>
              </tr>
            ) : displayedRides.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-12 text-gray-400">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Nuk ka porosi</p>
                </td>
              </tr>
            ) : (
              displayedRides.map((ride) => {
                const status = getStatus(ride.status);
                const destination = getRideDestination(ride);
                const priceDisplay = formatRidePriceLek(ride);
                const source = getRideSource(ride);
                const adSource = getAdAttribution(ride);
                const canComplete = ['assigned', 'en_route', 'accepted', 'arriving', 'in_progress', 'arrived_pickup', 'passenger_onboard', 'arrived_destination'].includes(ride.status);
                return (
                  <tr
                    key={ride.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => onViewDetails?.(ride)}
                  >
                    {/* ID */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-700">
                        {ride.ticket_number}
                      </span>
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm">
                              {ride.passenger_name || 'Walk-in'}
                            </span>
                            {source && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${source.className}`}>
                                {source.label}
                              </span>
                            )}
                            {adSource && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${adSource.className}`}>
                                {adSource.text}
                              </span>
                            )}
                          </div>
                          {ride.passenger_phone && (
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {ride.passenger_phone}
                            </div>
                          )}
                          {(ride.passenger_email || ride.customer_email) && (
                            <div className="text-xs text-gray-500">
                              {ride.passenger_email || ride.customer_email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* When the booking was created */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">
                        {ride.created_at ? formatDateTime(ride.created_at) : '—'}
                      </div>
                    </td>

                    {/* Destination */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate max-w-[200px]" title={destination}>
                          {destination}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 ml-6">
                        Nga: {getRidePickup(ride)}
                      </div>
                      <div className="text-xs text-gray-400 ml-6">
                        {ride.passenger_count} pasagjerë • {ride.vehicle_type === 'van' ? 'Van' : 'Sedan'}
                      </div>
                    </td>

                    {/* Driver */}
                    <td className="px-4 py-3">
                      {ride.driver_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                            <Car className="w-4 h-4 text-yellow-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{ride.driver_name}</div>
                            <div className="text-xs text-gray-500">
                              {ride.taxi_id && <span className="text-red-600 font-medium">{ride.taxi_id}</span>}
                              {ride.vehicle_plate && <span className="ml-1">| {ride.vehicle_plate}</span>}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Pa caktuar</span>
                      )}
                    </td>

                    {/* Trip date (scheduled pickup or order time) */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        {formatDateTime(getRideScheduledAt(ride) || ride.created_at)}
                      </div>
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3 text-right">
                      <div className="font-bold text-gray-900">{priceDisplay}</div>
                      {ride.price_eur > 0 && ride.price_determined !== false && (
                        <div className="text-xs text-gray-500">€{ride.price_eur}</div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                        {status.label}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        {ride.status === 'pending' && (
                          <>
                            {showAutoAssign && queueHasDrivers && (
                              <button
                                onClick={() => onAutoAssign?.(ride.id)}
                                className="p-1.5 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition"
                                title="Auto-cakto (i pari në radhë)"
                                data-testid={`auto-assign-${ride.id}`}
                              >
                                <Zap className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => onAssign?.(ride)}
                              className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition"
                              title="Cakto Shofer Manualisht"
                              data-testid={`manual-assign-${ride.id}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onCancel?.(ride.id)}
                              className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                              title="Anulo"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {canComplete && (
                          <button
                            onClick={() => onComplete?.(ride.id)}
                            className="p-1.5 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition"
                            title="Përfundo"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {!['completed', 'cancelled'].includes(ride.status) && onCancel && ride.status !== 'pending' && (
                          <button
                            onClick={() => onCancel?.(ride.id)}
                            className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                            title="Anulo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {onViewDetails && (
                          <button
                            onClick={() => onViewDetails(ride)}
                            className="p-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg transition"
                            title="Shiko detajet"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {onPrint && (
                          <button
                            onClick={() => onPrint(ride)}
                            className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                            title="Printo"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                        )}
                        {showDelete && onDelete && (
                          <button
                            onClick={() => onDelete(ride.id)}
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition border border-red-200"
                            title="Fshi porosinë (admin)"
                            data-testid={`delete-ride-${ride.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with summary */}
      {filteredRides.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500 flex justify-between items-center">
          <span>
            Duke shfaqur {filteredRides.length} porosi
          </span>
          <div className="flex gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              Në Pritje: {rides.filter(r => r.status === 'pending').length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
              Në Rrugë: {rides.filter(r => ['en_route', 'assigned', 'accepted', 'arriving', 'in_progress'].includes(r.status)).length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Përfunduar: {rides.filter(r => r.status === 'completed').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveOrdersTable;
